import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppContext, useHttp } from '@veda/dashboard-ui';
import LoginPannelloSinistro from '../components/LoginPannelloSinistro';

function ErrorBox({ msg }) {
  return (
    <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
      {msg}
    </div>
  );
}

const inputClass =
  'w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none ' +
  'focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm';

/**
 * Reimpostazione password da link ricevuto via mail.
 * URL: /reset-password?token=... — il token arriva come query param, così come
 * lo genera SendPasswordResetEmail lato Veda.
 */
export default function ResetPasswordPage() {
  const http = useHttp();
  const navigate = useNavigate();
  const { loginImage, currentMode } = useAppContext();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [stato, setStato] = useState(token ? 'verifica' : 'token-mancante');
  const [email, setEmail] = useState('');
  const [form, setForm] = useState({ password: '', password_confirmation: '' });
  const [errore, setErrore] = useState(null);

  const aggiorna = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  // Verifica del token con fetch diretto e non con useHttp: su 401 (token scaduto)
  // useHttp invoca logOut(), che azzererebbe la sessione di questa scheda —
  // effetto indesiderato su una pagina pubblica raggiunta da un link via mail.
  useEffect(() => {
    if (!token) return;

    const backend = window.VEDA_API_URL || '';
    let annullato = false;

    fetch(`${backend}/api/dashboard/verify-token/${encodeURIComponent(token)}`, {
      headers: { Accept: 'application/json' },
    })
      .then(async (res) => {
        if (annullato) return;
        if (!res.ok) { setStato('token-scaduto'); return; }
        const body = await res.json().catch(() => ({}));
        setEmail(body?.email ?? '');
        setStato('pronto');
      })
      .catch(() => { if (!annullato) setStato('token-scaduto'); });

    return () => { annullato = true; };
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErrore(null);

    if (form.password !== form.password_confirmation) {
      setErrore('Le password non coincidono.');
      return;
    }

    await http.request('/sanctum/csrf-cookie', { method: 'get' });
    await http.request('/api/dashboard/reset-password', {
      method: 'post',
      body: JSON.stringify({ email, token, ...form }),
      onSuccess: () => setStato('completato'),
      onFormError: (res) =>
        setErrore(res?.message ?? 'Impossibile reimpostare la password. Il link potrebbe essere scaduto.'),
      onError: (err) =>
        setErrore(err instanceof Error ? err.message : 'Errore durante la reimpostazione.'),
    });
  }

  // Funzione, non componente: se fosse reso come <Contenuto /> React vedrebbe a
  // ogni battitura un tipo nuovo, rimonterebbe gli input e l'autoFocus del primo
  // campo riporterebbe lì il cursore.
  function contenuto() {
    if (stato === 'verifica') {
      return <p className="text-sm text-gray-500 dark:text-gray-400">Verifica del link in corso…</p>;
    }

    if (stato === 'token-mancante' || stato === 'token-scaduto') {
      return (
        <div className="flex flex-col gap-4">
          <ErrorBox
            msg={
              stato === 'token-mancante'
                ? 'Link non valido: manca il token di reimpostazione.'
                : 'Questo link non è più valido o è scaduto. Richiedi una nuova mail di recupero.'
            }
          />
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors text-sm"
          >
            Torna al login
          </button>
        </div>
      );
    }

    if (stato === 'completato') {
      return (
        <div className="flex flex-col gap-4">
          <div className="px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
            Password reimpostata correttamente. Ora puoi accedere con le nuove credenziali.
          </div>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors text-sm"
          >
            Vai al login
          </button>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {errore && <ErrorBox msg={errore} />}

        {email && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Account: <span className="font-semibold text-gray-700 dark:text-gray-200">{email}</span>
          </p>
        )}

        <input
          type="password" name="password" value={form.password} onChange={aggiorna}
          placeholder="Nuova password" required minLength={8} autoFocus
          className={inputClass}
        />
        <input
          type="password" name="password_confirmation" value={form.password_confirmation} onChange={aggiorna}
          placeholder="Conferma nuova password" required minLength={8}
          className={inputClass}
        />
        <p className="text-xs text-gray-400">Almeno 8 caratteri.</p>

        <button
          type="submit" disabled={http.loading}
          className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
        >
          {http.loading ? 'Salvataggio in corso…' : 'Reimposta password'}
        </button>
      </form>
    );
  }

  return (
    <div className={currentMode}>
      <div className="min-h-screen flex dark:bg-main-dark-bg bg-main-bg">
        <LoginPannelloSinistro loginImage={loginImage} />

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-sm">
            <div className="mb-6">
              <h1 className="text-2xl font-extrabold text-gray-800 dark:text-white mb-1">Reimposta password</h1>
              <p className="text-sm text-gray-400">Scegli una nuova password per il tuo account.</p>
            </div>

            {contenuto()}
          </div>
        </div>
      </div>
    </div>
  );
}
